/**
 * Convert degrees to radians
 * @param {number} deg - Angle in degrees
 * @returns {number} Angle in radians
 */
export function deg2rad(deg) {
    return deg * (Math.PI / 180);
}

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lng1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lng2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = deg2rad(lat2 - lat1);
    const dLng = deg2rad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Calculate similarity score between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity score between 0 and 1
 */
export function getSimilarityScore(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    const longerLength = longer.length;
    if (longerLength === 0) {
        return 1.0; // Two empty strings are considered identical
    }
    return (longerLength - getEditDistance(longer, shorter)) / parseFloat(longerLength);
}

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance
 */
function getEditDistance(str1, str2) {
    const tmp = [];
    let i, j,
        l1 = str1.length,
        l2 = str2.length,
        al = l1 + 1,
        bl = l2 + 1;

    for (i = 0; i < al; i++) tmp[i] = [i];
    for (j = 0; j < bl; j++) tmp[0][j] = j;

    for (i = 1; i < al; i++) {
        for (j = 1; j < bl; j++) {
            tmp[i][j] =
                str1[i - 1] === str2[j - 1]
                    ? tmp[i - 1][j - 1]
                    : Math.min(
                        tmp[i - 1][j - 1] + 1,
                        Math.min(tmp[i][j - 1] + 1, tmp[i - 1][j] + 1)
                    );
        }
    }
    return tmp[l1][l2];
}

/**
 * Find and sort hotels by distance and name similarity
 * @param {number} placeLat - Place latitude
 * @param {number} placeLng - Place longitude
 * @param {Array} hotelData - Array of hotel objects
 * @param {string} locationName - Location name to compare with hotel names
 * @returns {Array} Sorted array of hotels with distance and similarity information
 */
export function findHotelsByDistanceAndSimilarity(placeLat, placeLng, hotelData, locationName) {
    const SIMILARITY_THRESHOLD = 0.65; // 65% similarity threshold
    const hotelsWithDistance = hotelData.map((hotel) => {
        const distance = calculateDistance(
            placeLat,
            placeLng,
            parseFloat(hotel.latitude || 0),
            parseFloat(hotel.longitude || 0)
        );
        const similarityScore = getSimilarityScore(
            locationName?.toLowerCase() || "",
            hotel.name?.toLowerCase() || ""
        );
        const isSimilar = similarityScore >= SIMILARITY_THRESHOLD;
        return { ...hotel, distance, isSimilar, similarityScore };
    });
    hotelsWithDistance.sort((a, b) => a.distance - b.distance);

    return hotelsWithDistance;
} 